import { format as fnsFormat, parseISO, intervalToDuration } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

const localeFor = (lang: string) => (lang.startsWith('pl') ? pl : enUS);

const toDate = (value: string | Date): Date =>
  typeof value === 'string' ? parseISO(value) : value;

// Long date: "14 maj 2026" / "May 14, 2026"
export function formatDate(value: string | Date | null | undefined, lang: string): string {
  if (!value) return '';
  return fnsFormat(toDate(value), 'PP', { locale: localeFor(lang) });
}

// Long date + time: "14 maj 2026, 14:32" / "May 14, 2026, 2:32 PM"
export function formatDateTime(value: string | Date | null | undefined, lang: string): string {
  if (!value) return '';
  return fnsFormat(toDate(value), 'PPp', { locale: localeFor(lang) });
}

// Short date: "14.05.2026" / "05/14/2026"
export function formatDateShort(value: string | Date | null | undefined, lang: string): string {
  if (!value) return '';
  return fnsFormat(toDate(value), 'P', { locale: localeFor(lang) });
}

// Time only: "14:32" / "2:32 PM"
export function formatTime(value: string | Date | null | undefined, lang: string): string {
  if (!value) return '';
  return fnsFormat(toDate(value), 'p', { locale: localeFor(lang) });
}

// Month + year: "maj 2026" / "May 2026"
export function formatMonth(value: string | Date | null | undefined, lang: string): string {
  if (!value) return '';
  return fnsFormat(toDate(value), 'LLLL yyyy', { locale: localeFor(lang) });
}

// Duration in minutes → "1h 30min" / "45min" / "2h"
export function formatDurationMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

// Elapsed time from a start instant → "2h 13m 47s" or "13m 47s" (always includes seconds)
export function formatElapsedSince(startedAt: string | Date): string {
  const start = typeof startedAt === 'string' ? parseISO(startedAt) : startedAt;
  const end = new Date(Math.max(start.getTime(), Date.now()));
  const { hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start, end });
  return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
}

// Hook variant — reads current i18n language. Use this in components.
export function useDateFormat() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return {
    formatDate: (value: string | Date | null | undefined) => formatDate(value, lang),
    formatDateTime: (value: string | Date | null | undefined) => formatDateTime(value, lang),
    formatDateShort: (value: string | Date | null | undefined) => formatDateShort(value, lang),
    formatTime: (value: string | Date | null | undefined) => formatTime(value, lang),
    formatMonth: (value: string | Date | null | undefined) => formatMonth(value, lang),
  };
}
